import * as React from 'react';
import { useState } from 'react';
import { PropagateLoader } from 'react-spinners';
import Modal from '../../shared/Modal';
import { search, IAccountSearchResult, IAccountDetails } from '../../services/account';
import { add } from '../../services/partner'
import { defineMessages, FormattedMessage, injectIntl, InjectedIntlProps, FormattedHTMLMessage } from 'react-intl';
import DatabaseSelect from '../shared/DatabaseSelect';

const addButtonStyle = {
    cursor: 'pointer',
    margin: '0',
    paddingTop: 3,
    paddingBottom: 3
};

export default injectIntl((props: InjectedIntlProps & { onSave: () => void, accountDetails: IAccountDetails, buttonClassName?: string, buttonStyle?: any }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [partnerResults, setPartnerResults] = useState<IAccountSearchResult[]>([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(false);
    const [selectedAccounts, setSelectedAccounts] = useState<IAccountSearchResult[]>([]);
    const [selectedDatabaseName, setSelectedDatabaseName] = useState<string | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

    const messages = defineMessages({
        serverError: {
            id: 'dash.partner.add.err',
            defaultMessage: 'There was an error contacting the server. Please reload the page and try again.'
        },
        searchOrgs: {
            id: 'partner.add.search.placeholder',
            defaultMessage: 'Search Organizations'
        },
        cancelSendMessage: {
            id: 'partner.add.cancel',
            defaultMessage: 'Cancel'
        }
    });

    const invitePartner = async (account: IAccountSearchResult, noDbAlert: string, addAlert: string) => {
        if (!selectedDatabaseName) {
            alert(noDbAlert)
            return
        }
        if (confirm(addAlert)) {
            setSelectedAccounts([...selectedAccounts, { ...account }])
            await add(account.name, selectedDatabaseName)
        }
    };

    const renderPartnerButton = (isSelected: boolean, result: IAccountSearchResult) => {
        if (isSelected) {
            return (
                <div className="btn-success" style={addButtonStyle}>
                    <i className="fas fa-check" />
                </div>
            )
        }
        return (
            <FormattedMessage id="dash.partner.add.nobd.error" defaultMessage="Please select a database to add a partner!">
                {
                    (noDbError: string) => (
                        <FormattedMessage id="dash.partner.add.confirm" values={{ name: result.organization }} defaultMessage="Add {name} as a partner to your database?">
                            {
                                (addAlert: string) => (
                                    <button type="button" className="primary" style={addButtonStyle} onClick={() => invitePartner(result, noDbError, addAlert)}>
                                        <i className="fas fa-user-plus" />
                                    </button>
                                )
                            }
                        </FormattedMessage>
                    )
                }
            </FormattedMessage>
        )
    }

    const renderPartnerSearchResult = (index: number, result: IAccountSearchResult, force: boolean = false) => (force || selectedAccounts.findIndex(x => result.name === x.name) === -1) && (
        <tr key={`${result.email}-${index}`}>
            <td>{result.organization}</td>
            <td>{result.city}</td>
            <td style={{ textAlign: 'center' }}>
                {renderPartnerButton(selectedAccounts.findIndex(x => result.name === x.name) > -1, result)}
            </td>
        </tr>
    );

    const renderSearchResults = () => {
        if ((!partnerResults || !partnerResults.length) && (!selectedAccounts || !selectedAccounts.length)) {
            return <noscript />;
        }
        return (
            <table className="table">
                <thead>
                    <tr>
                        <th scope="col"><FormattedMessage id="dash.partner.add.name" defaultMessage="Name" /></th>
                        <th scope="col"><FormattedMessage id="dash.partner.add.city" defaultMessage="City" /></th>
                        <th scope="col" style={{ textAlign: 'center' }}><FormattedMessage id="dash.partner.add.add" defaultMessage="Add" /></th>
                    </tr>
                </thead>
                <tbody>
                    {selectedAccounts.map((x, i) => renderPartnerSearchResult(i, x))}
                    {partnerResults.map((x, i) => renderPartnerSearchResult(i, x, true))}
                </tbody>
            </table>
        );
    };

    const updateSearchTerm = async (e: React.FormEvent<HTMLInputElement>) => {
        const newTerm = e.currentTarget.value;
        setSearchTerm(newTerm);
        setIsLoadingSearch(true);
        try {
            const req = await search(newTerm);
            const results = req.results;
            if (!results) return;
            setPartnerResults(results);
            setIsLoadingSearch(false);
        } catch (ex) {
            console.error(ex);
            alert(props.intl.formatMessage(messages.serverError));
            setIsLoadingSearch(false);
        }
    };

    const renderDatabaseSelect = () => {
        return (
            // <FormattedMessage id="dash.partner.add.nodb" defaultMessage="You must have a database to invite partners to join!">
            //     {(noDatabasesMessage: string) => (
            //         <FormattedMessage id="dash.partner.add.dbselect" defaultMessage="Select a Database to Share">
            //             {(defaultMessage: string) => }
            //         </FormattedMessage>
            //     )
            //     }
            // </FormattedMessage>
            <DatabaseSelect
                hidePartnerDatabases
                errorMessage={!selectedDatabaseName && hasSubmitted && "You must select a database to add a partner."}
                currentModalId="invitePartnersModal"
                onChange={setSelectedDatabaseName}
                defaultMessage={"Select a Database to Share"}
                canCreate={props.accountDetails && props.accountDetails.subscriptionLevel > 0}
                noDatabasesMessage={"You must have a database to invite partners to join!"} />
        )
    }

    const invite = () => {
        if (!selectedDatabaseName || !selectedAccounts.length) {
            setHasSubmitted(true);
            return;
        }
        $('#invitePartnersModal').modal('hide')
        props.onSave()
    }

    const invitePartnersActionButtons = [
        <button type="button" className="primary " key="done-button" onClick={invite} disabled={!selectedDatabaseName || !selectedAccounts.length}>
            <FormattedMessage id="dash.partner.invite.btn" defaultMessage="Add" />
        </button>
    ];

    const renderAccountSearch = () => {
        if (!selectedDatabaseName) {
            return <noscript />
        }
        return (
            <React.Fragment>
                <div className="input-group md-form form-sm form-2 pl-0">
                    <input
                        className="form-control my-0 py-1"
                        type="text"
                        placeholder={props.intl.formatMessage(messages.searchOrgs)}
                        aria-label={props.intl.formatMessage(messages.searchOrgs)}
                        onChange={updateSearchTerm}
                        value={searchTerm}
                    />
                    <div className="input-group-append">
                        <span
                            className="input-group-text lime lighten-2"
                            id="basic-text1">
                            <i className="fas fa-search text-grey" aria-hidden="true" />
                        </span>
                    </div>
                </div>
                <div className="d-flex flex-column">
                    {renderSearchResults()}
                    <div className="pt-5 d-flex justify-content-center">
                        <PropagateLoader size={15} loading={isLoadingSearch} color="#29447b" />
                    </div>
                </div>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <button
                className={`primary h3 px-3 py-2 m-0 mx-3 ${props.buttonClassName}`}
                data-toggle="modal"
                style={props.buttonStyle || {}}
                data-target="#invitePartnersModal">
                <FormattedMessage id="dash.partner.add.invite" defaultMessage="Add a New Partner" />
            </button>
            <Modal
                large
                id="invitePartnersModal"
                title="Add A New Partner"
                actionButtons={invitePartnersActionButtons}>
                <p className="text-left"><FormattedHTMLMessage id="dash.partner.invite.text" defaultMessage={`
                    Please type in the name of the organization you wish to partner with in the search box below.<br/>
                    Click the <strong>Add</strong> button to invite that organization as a partner for that database.
                    They will receive a notification of your invitation to partner and must accept it to link
                    with your account and add resources to your database.`} /></p>
                <div className="form-group md-form d-flex justify-content-center">
                    {renderDatabaseSelect()}
                </div>
                {renderAccountSearch()}

            </Modal>
        </React.Fragment >
    );
});



// WEBPACK FOOTER //
// ./src/client/dashboard/AddPartnerModal.tsx